import React, { useEffect, useState, useMemo } from "react";

import "./course.quiz.cmp.scss";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import courseComander from "../../../store/commanders/course.comander";
import CircularProgressCmp from "../../common/progressbar/circular.progress.cmp";
import { ac_get_answers } from "../../../store/actions/course.action";
import {
  cleanModuleTitle,
  findRelatedVideoForQuiz,
} from "../../../helpers/course.helper";
import PLButton from "../../common/button/PLButton";
import { TestTubeDiagonal } from "lucide-react";

const normalizeReviewedAnswers = (response: any): Record<string, string> => {
  const rawAnswers = response?.response_data ?? response;

  if (
    !rawAnswers ||
    typeof rawAnswers !== "object" ||
    Array.isArray(rawAnswers)
  ) {
    return {};
  }

  return rawAnswers as Record<string, string>;
};

const CourseQuestionCmp = ({
  item,
  register,
  errors,
  reviewMode,
  rightAnswers,
  userAnswer,
  correctAnswerId,
}) => {
  const renderPossibleAnswers = (answers) => {
    const nodotId = item.id.replaceAll(".", "#");
    return answers.map((answer, index) => {
      return (
        <label key={answer.id} className="quiz-answer" id={answer.id}>
          <input
            className="quiz-radiobox"
            type="radio"
            value={answer.id}
            id={answer.id}
            {...register(nodotId, {
              required: true,
              disabled: reviewMode,
            })}
          />
          {answer.title}
        </label>
      );
    });
  };

  return (
    <div className="quiz-question" id={item.id}>
      <div className="quiz-question-title">{item.title}</div>
      {renderPossibleAnswers(item.possibleAnswers)}
      {errors[item.title] && <span>This field is required</span>}
    </div>
  );
};

const CourseQuizCmp = ({ selected, isUserAdmin }) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
    control,
    setValue,
  } = useForm();
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewedAnswers, setReviewedAnswers] = useState<
    Record<string, string>
  >({});
  const [loadingAnswers, setLoadingAnswers] = useState(false);

  // Mobile quiz state management
  const [isMobile, setIsMobile] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState(new Set());
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [showValidationError, setShowValidationError] = useState(false);

  // Ref for scrolling to quiz title
  const quizTitleRef = React.useRef<HTMLHeadingElement>(null);

  // Determine if we should use mobile single-question mode
  const isMobileQuiz = isMobile && !reviewMode;
  const answeredQuestionsCount = useMemo(() => {
    if (!selected?.children?.length || typeof selected?.grades !== "number") {
      return 0;
    }

    return Math.round((selected.grades * selected.children.length) / 100);
  }, [selected?.children?.length, selected?.grades]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia(
      "(max-width: 1024px), (pointer: coarse)",
    );
    const updateIsMobile = () => {
      const userAgent = window.navigator.userAgent || "";
      const isTouchDevice =
        window.navigator.maxTouchPoints > 0 ||
        "ontouchstart" in window ||
        mediaQuery.matches;
      const isMobileUserAgent =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent,
        );

      setIsMobile(
        window.innerWidth <= 1024 || isTouchDevice || isMobileUserAgent,
      );
    };

    updateIsMobile();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateIsMobile);
    } else {
      mediaQuery.addListener(updateIsMobile);
    }

    window.addEventListener("resize", updateIsMobile);

    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", updateIsMobile);
      } else {
        mediaQuery.removeListener(updateIsMobile);
      }
      window.removeEventListener("resize", updateIsMobile);
    };
  }, []);

  // Memoize the cleaned quiz title to only recalculate when selected changes
  const cleanedTitle = useMemo(() => {
    return cleanModuleTitle(selected.title || "");
  }, [selected.title]);

  // Check if the related video is watched
  const relatedVideo = useMemo(
    () => findRelatedVideoForQuiz(selected.id),
    [selected.id],
  );
  const isVideoWatched = true; //relatedVideo?.finish || false;

  const onSubmit = (data) => {
    const userAnswers = {};
    Object.entries(data).forEach(([key, value]) => {
      userAnswers[key.replaceAll("#", ".")] = value;
    });
    courseComander.ac_submit_quiz(userAnswers);
    scrollToTop();
  };

  // Shuffle questions once when quiz loads
  useEffect(() => {
    if (selected?.children) {
      const shuffled = [...selected.children].sort(() => {
        const randomTrueOrFalse = Math.random() > 0.5;
        return randomTrueOrFalse ? 1 : -1;
      });
      setShuffledQuestions(shuffled);
      setCurrentQuestionIndex(0);
      setAnsweredQuestions(new Set());
    }
  }, [selected?.id]);

  useEffect(() => {
    reset();
    if (reviewMode && selected) {
      setReviewMode(false);
      setReviewedAnswers({});
    }
  }, [selected?.id]);

  useEffect(() => {
    async function getAnswers() {
      try {
        const response = await ac_get_answers(selected.id);
        setReviewedAnswers(normalizeReviewedAnswers(response));
      } catch (e) {}
      setLoadingAnswers(false);
    }

    if (reviewMode && selected) {
      setLoadingAnswers(true);
      getAnswers();
    }
  }, [reviewMode]);

  useEffect(() => {
    if (reviewMode && selected) {
      Object.entries(reviewedAnswers || {}).forEach(
        ([answerId, answerValue]) => {
          setValue(answerId.replaceAll(".", "#"), answerValue);
        },
      );
    }
  }, [reviewedAnswers]);

  // Track answered questions by watching form values
  const formValues = watch();

  // Memoize the stringified form values to prevent infinite loops
  const formValuesString = useMemo(
    () => JSON.stringify(formValues),
    [formValues],
  );

  useEffect(() => {
    const answered = new Set();
    shuffledQuestions.forEach((question) => {
      const questionId = question.id.replaceAll(".", "#");
      if (formValues[questionId]) {
        answered.add(question.id);
      }
    });
    setAnsweredQuestions(answered);

    // Clear validation error if current question is answered
    if (
      shuffledQuestions[currentQuestionIndex] &&
      formValues[
        shuffledQuestions[currentQuestionIndex].id.replaceAll(".", "#")
      ]
    ) {
      setShowValidationError(false);
    }
  }, [formValuesString, shuffledQuestions, currentQuestionIndex]);

  // Scroll quiz title into view
  const scrollToTop = () => {
    if (quizTitleRef.current) {
      quizTitleRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  // Navigation handlers for mobile quiz
  const handleNext = () => {
    const currentQ = shuffledQuestions[currentQuestionIndex];
    if (!answeredQuestions.has(currentQ?.id)) {
      setShowValidationError(true);
      return;
    }

    if (currentQuestionIndex < shuffledQuestions.length - 1) {
      setShowValidationError(false);
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      scrollToTop();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      scrollToTop();
    }
  };

  const renderQuestions = (questions) => {
    // Use pre-shuffled questions or fallback to questions param
    const questionsToRender =
      shuffledQuestions.length > 0 ? shuffledQuestions : questions;

    // Helper to get correct answer for a question
    const getCorrectAnswer = (question) => {
      // Find the correct answer from possibleAnswers
      const correctAnswer = question.possibleAnswers?.find(
        (ans) => ans.correct === true,
      );
      return correctAnswer?.id;
    };

    // On mobile (not review mode), show only current question
    if (isMobileQuiz && questionsToRender.length > 0) {
      const currentQuestion = questionsToRender[currentQuestionIndex];
      return (
        <div className={showValidationError ? "question-validation-error" : ""}>
          <CourseQuestionCmp
            key={currentQuestion.id}
            item={currentQuestion}
            register={register}
            errors={errors}
            reviewMode={reviewMode}
            rightAnswers={reviewedAnswers}
            userAnswer={reviewedAnswers?.[currentQuestion.id]}
            correctAnswerId={getCorrectAnswer(currentQuestion)}
          />
          {showValidationError && (
            <div className="validation-error-message">
              Please select an answer before proceeding
            </div>
          )}
        </div>
      );
    }

    // Desktop or review mode: show all questions
    return questionsToRender.map((item) => (
      <CourseQuestionCmp
        key={item.id}
        item={item}
        register={register}
        errors={errors}
        reviewMode={reviewMode}
        rightAnswers={reviewedAnswers}
        userAnswer={reviewedAnswers?.[item.id]}
        correctAnswerId={getCorrectAnswer(item)}
      />
    ));
  };

  return (
    // IMPORTANT: .quiz-container class is used by course tree for scroll-to-top - don't remove
    <div className="quiz-container">
      {/* Quiz Title Header with Next Button */}
      <div className="quiz-header">
        <h2 className="quiz-title" ref={quizTitleRef}>
          {cleanedTitle}
        </h2>
        {(selected.status === "pass" || reviewMode) && (
          <PLButton
            onClick={() => courseComander.ac_next_lesson()}
            variant="primary"
            size="lg"
            className="next-lesson-btn"
          >
            Next
          </PLButton>
        )}
      </div>
      {/* Show quiz start screen if video not watched */}
      {!isVideoWatched && selected.status === "unknown" ? (
        <div className="quiz-start-screen">
          <div className="quiz-start-content">
            <div className="quiz-icon">
              <TestTubeDiagonal size={48} />
            </div>
            <h3>Quiz: {cleanedTitle}</h3>
            <p>
              To take this quiz, you must first complete the video lesson in
              this module.
            </p>
            <div className="video-requirement">
              <strong>Required:</strong> Watch "{cleanedTitle}" video to unlock
              this quiz
            </div>
            <PLButton
              onClick={() => {
                if (relatedVideo) {
                  courseComander.cmd_select_section(relatedVideo);
                }
              }}
              variant="primary"
              size="lg"
              className="watch-video-btn"
            >
              Complete Video Lesson
            </PLButton>
          </div>
        </div>
      ) : selected.children && (selected.status == "unknown" || reviewMode) ? (
        <form onSubmit={handleSubmit(onSubmit)}>
          {loadingAnswers ? (
            <div className="loader loader-center"></div>
          ) : (
            <>
              {/* Mobile Progress Indicator */}
              {isMobileQuiz && shuffledQuestions.length > 0 && (
                <div className="quiz-progress-header">
                  <div className="quiz-progress-text">
                    Question {currentQuestionIndex + 1} of{" "}
                    {shuffledQuestions.length}
                  </div>
                  <div className="quiz-progress-bar">
                    <div
                      className="quiz-progress-fill"
                      style={{
                        width: `${((currentQuestionIndex + 1) / shuffledQuestions.length) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {renderQuestions(selected.children)}

              {/* Mobile Navigation */}
              {isMobileQuiz && shuffledQuestions.length > 0 && (
                <div className="quiz-navigation-mobile">
                  <PLButton
                    type="button"
                    onClick={handlePrevious}
                    disabled={currentQuestionIndex === 0}
                    variant="secondary"
                    size="md"
                    className="nav-btn nav-btn-prev"
                  >
                    Previous
                  </PLButton>

                  {currentQuestionIndex === shuffledQuestions.length - 1 ? (
                    <PLButton
                      type="submit"
                      variant="primary"
                      size="md"
                      className="nav-btn nav-btn-submit"
                    >
                      Submit
                    </PLButton>
                  ) : (
                    <PLButton
                      type="button"
                      onClick={handleNext}
                      variant="primary"
                      size="md"
                      className="nav-btn nav-btn-next"
                    >
                      Next
                    </PLButton>
                  )}
                </div>
              )}
            </>
          )}

          {/* Submit buttons */}
          <div className="quiz-button-container">
            {!reviewMode && (
              <div className={"quiz-btns"}>
                <PLButton type="submit" variant="primary" size="lg">
                  Submit
                </PLButton>
                {isUserAdmin ? (
                  <PLButton
                    onClick={() => courseComander.cmd_admin_pass_quiz()}
                    variant="primary"
                    size="lg"
                    className="pass-quiz-btn"
                  >
                    Pass
                  </PLButton>
                ) : null}
              </div>
            )}
          </div>
        </form>
      ) : null}

      {(selected.status === "pass" || selected.status === "fail") &&
      !reviewMode ? (
        <div className="grade-percentage">
          <div className="grade-percentage__shape">
            <CircularProgressCmp
              grades={selected.grades}
              status={selected.status}
            />
          </div>
          <h1
            style={{
              color: selected.status === "pass" ? "var(--color-success)" : "var(--color-error)",
              textTransform: "capitalize",
            }}
          >
            {selected.status === "fail" ? "incomplete" : selected.status}
          </h1>

          <h3 className=" u-mt-md">
            You answered {answeredQuestionsCount} of {selected.children.length}{" "}
            Questions Correctly
          </h3>

          {selected.status === "pass" ? (
            <div className="quiz-btns u-mt-xl">
              <PLButton
                onClick={() => setReviewMode(true)}
                variant="secondary"
                size="lg"
              >
                Review
              </PLButton>
            </div>
          ) : (
            <PLButton
              onClick={() => {
                setCurrentQuestionIndex(0);
                setShowValidationError(false);
                reset();
                courseComander.cmd_retry_quiz(selected);
              }}
              variant="primary"
              size="lg"
            >
              Retry
            </PLButton>
          )}
        </div>
      ) : null}
    </div>
  );
};
export default CourseQuizCmp;
